// File: app/src/utils/trainingPreferencesPrompt.js
//
// Builds the "Training Preferences" block of the workout-generation prompt (see
// buildPrompt in app/src/apis/llm/prompts.js) from the athlete's standing
// preferences in their profile (config/trainingPreferences.js).
//
// These exist because a preference like "I don't want to stand around between
// sets" cannot be expressed by picking a style. Choosing Superset each time
// only covers sessions that are supersets; pick Strength and the style spec
// still demands one Group per exercise with minutes of idle rest between sets. So
// the paired and active rules are written as explicit, scoped OVERRIDES of the
// straight-set styles. Left as a soft note, the model would be holding two
// contradictory instructions and resolves that toward the more specific one,
// which is the style spec.
//
// The section is absent entirely at the defaults, so an untouched profile
// produces exactly the prompt it always did.

import {
  DEFAULT_REST_STYLE,
  DEFAULT_SESSION_PACE,
  MAX_TRAINING_PREFERENCES_LENGTH,
} from "../config/trainingPreferences";

// Styles whose spec prescribes idle inter-set rest for a single exercise —
// the only places "what fills the rest" has anything to change.
const STRAIGHT_SET_STYLES = ["strength", "hypertrophy", "power"];

const formatStyleList = (styles) => {
  const names = styles.map((s) => s.charAt(0).toUpperCase() + s.slice(1));
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
};

// Agonist-antagonist supersets keep volume and chronic strength intact (Zhang
// 2025), so heavy lifts may pair — just not with another heavy lift. Power is
// the exception: fatigue costs the speed that is the whole point.
const pairedRule = (styleNames) =>
  `- Between sets: PAIR EXERCISES. The user does not want to stand idle between sets. In every ${styleNames} block, pair each exercise with a second exercise for a different muscle group or movement pattern (push with pull, upper with lower — e.g. Bench Press with Bent-over Row) instead of resting. This OVERRIDES the "ONE Group per exercise" rule in the Style Rule: author each pair as ONE Group(unit="sets", value=N) holding both Work nodes back-to-back, then a SINGLE Rest at the end of the round. The partner exercise counts toward each muscle's recovery, so shorten that Rest to keep each muscle within the style's normal recovery rather than adding the two together. For heavy strength sets, the partner is an opposing-muscle exercise at a moderate load, never a second near-maximal lift. For explosive Power work, pair only with a low-fatigue mobility or core drill so bar speed is not lost.`;

const activeRule = (styleNames) =>
  `- Between sets: ACTIVE REST. The user prefers to keep moving rather than stand idle. In every ${styleNames} block, fill most of the inter-set recovery with a light mobility, core or activation drill that does not tire the muscles being trained (e.g. a hip-flexor stretch between squat sets, a dead bug between presses). Author it inside the same Group, after the main Work: Work(unit="s", 30-60 s, autoStart=true), then a shorter Rest, so the total recovery between sets stays within the style's normal range.`;

const denseRule = `- Pace: DENSE. The user wants a high-energy session with plenty of movement. Keep every Rest at the LOW end of its style's range, prefer compound and full-body movements over isolation work, and spend the time budget on more work rather than more rest. Never cut rest below what safe technique on a heavy lift needs.`;

/**
 * @param {Object} preferences
 * @param {string} [preferences.restStyle]           'full' | 'paired' | 'active'
 * @param {string} [preferences.sessionPace]         'steady' | 'dense'
 * @param {string} [preferences.trainingPreferences] free text from the profile
 * @param {string[]} [preferences.styles]            the session's resolved styles
 * @returns {string} The prompt section, or "" when nothing differs from default.
 */
export const buildTrainingPreferencesSection = ({
  restStyle = DEFAULT_REST_STYLE,
  sessionPace = DEFAULT_SESSION_PACE,
  trainingPreferences = "",
  styles = [],
} = {}) => {
  const rules = [];

  // A rest-style rule only lands where there is idle inter-set rest to fill.
  // A circuit or HIIT session has none, and a rule with nothing to act on just
  // invites the model to restructure blocks that were already right.
  const straightSetStyles = STRAIGHT_SET_STYLES.filter((s) =>
    (styles || []).some((style) => String(style).toLowerCase() === s),
  );
  if (straightSetStyles.length > 0) {
    const styleNames = formatStyleList(straightSetStyles);
    if (restStyle === "paired") rules.push(pairedRule(styleNames));
    if (restStyle === "active") rules.push(activeRule(styleNames));
  }

  if (sessionPace === "dense") rules.push(denseRule);

  const freeText =
    typeof trainingPreferences === "string"
      ? trainingPreferences
          .replace(/\s+/g, " ")
          .trim()
          .slice(0, MAX_TRAINING_PREFERENCES_LENGTH)
      : "";
  if (freeText) {
    rules.push(
      `- In the user's own words: "${freeText}". Follow these where you reasonably can.`,
    );
  }

  if (rules.length === 0) return "";

  return `
## Training Preferences
How this user likes sessions put together. These apply across every style in the session. They are PREFERENCES, not constraints: injuries and limitations, today's session notes, any Programme Context and the time budget all take priority where they conflict.
${rules.join("\n")}
`;
};
